import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Bookmark, Columns3, Settings2, Trash2} from 'lucide-react';
import {useEffect, useRef, useState} from 'react';
import {apiRequest} from '../api';
import {Modal, PrimaryButton, SecondaryButton, TextInput} from '../design';
import {
  OPPORTUNITY_STAGES,
  TERMINAL_OPPORTUNITY_STAGES,
  OpportunityStage,
  humanizeStage,
} from '../types';

export const ALL_OPPORTUNITY_STAGES: OpportunityStage[] = [
  ...OPPORTUNITY_STAGES,
  ...TERMINAL_OPPORTUNITY_STAGES,
];

type SavedView = {
  id: string;
  name: string;
  query: string;
  visibleStages: OpportunityStage[];
};

type BoardPreferences = {
  groupBy: 'stage';
  visibleStages: OpportunityStage[];
  savedViews: SavedView[];
  activeViewId: string | null;
};

const defaults: BoardPreferences = {
  groupBy: 'stage',
  visibleStages: [...OPPORTUNITY_STAGES],
  savedViews: [],
  activeViewId: null,
};

export const OpportunityBoardControls = ({
  query,
  visibleStages,
  onApply,
}: {
  query: string;
  visibleStages: OpportunityStage[];
  onApply: (next: {query: string; visibleStages: OpportunityStage[]}) => void;
}) => {
  const queryClient = useQueryClient();
  const initialized = useRef(false);
  const [opened, setOpened] = useState(false);
  const [viewName, setViewName] = useState('');
  const [draftStages, setDraftStages] = useState<OpportunityStage[]>(visibleStages);
  const preferences = useQuery({
    queryKey: ['workspace', 'opportunity-board'],
    queryFn: () => apiRequest<BoardPreferences>('/workspace/opportunity-board'),
  });

  useEffect(() => {
    if (!preferences.data || initialized.current) return;
    initialized.current = true;
    const active = preferences.data.savedViews.find((view) => view.id === preferences.data?.activeViewId);
    onApply({
      query: active?.query ?? '',
      visibleStages: active?.visibleStages ?? preferences.data.visibleStages,
    });
    setDraftStages(active?.visibleStages ?? preferences.data.visibleStages);
  }, [onApply, preferences.data]);

  const persist = useMutation({
    mutationFn: (next: BoardPreferences) =>
      apiRequest<BoardPreferences>('/workspace/opportunity-board', {
        method: 'PATCH',
        body: JSON.stringify(next),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(['workspace', 'opportunity-board'], next);
    },
  });

  const current = preferences.data ?? defaults;

  const applyView = (viewId: string) => {
    const view = current.savedViews.find((candidate) => candidate.id === viewId);
    if (!view) {
      onApply({query: '', visibleStages: current.visibleStages});
      persist.mutate({...current, activeViewId: null});
      return;
    }
    onApply({query: view.query, visibleStages: view.visibleStages});
    setDraftStages(view.visibleStages);
    persist.mutate({...current, activeViewId: view.id});
  };

  const saveColumns = () => {
    if (!draftStages.length) return;
    onApply({query, visibleStages: draftStages});
    persist.mutate({...current, visibleStages: draftStages, activeViewId: null}, {
      onSuccess: () => setOpened(false),
    });
  };

  const saveView = () => {
    const name = viewName.trim();
    if (!name || !visibleStages.length) return;
    const existing = current.savedViews.find((view) => view.name.toLocaleLowerCase() === name.toLocaleLowerCase());
    const saved: SavedView = {
      id: existing?.id ?? crypto.randomUUID(),
      name,
      query,
      visibleStages,
    };
    const savedViews = existing
      ? current.savedViews.map((view) => view.id === existing.id ? saved : view)
      : [...current.savedViews, saved];
    persist.mutate({...current, visibleStages, savedViews, activeViewId: saved.id}, {
      onSuccess: () => {
        setViewName('');
        setOpened(false);
      },
    });
  };

  const removeActive = () => {
    if (!current.activeViewId) return;
    const savedViews = current.savedViews.filter((view) => view.id !== current.activeViewId);
    persist.mutate({...current, savedViews, activeViewId: null});
  };

  return (
    <>
      <div className="pd-board-view-controls">
        <label>
          <Bookmark size={15} aria-hidden="true" />
          <span className="pd-visually-hidden">Saved opportunity view</span>
          <select
            value={current.activeViewId ?? ''}
            disabled={preferences.isLoading || persist.isPending}
            onChange={(event) => applyView(event.currentTarget.value)}
          >
            <option value="">Current board</option>
            {current.savedViews.map((view) => <option key={view.id} value={view.id}>{view.name}</option>)}
          </select>
        </label>
        <span title="Opportunities are grouped by stable semantic stage">
          <Columns3 size={15} aria-hidden="true" />
          Grouped by stage
        </span>
        <button
          type="button"
          className="pd-board-config-button"
          onClick={() => {
            setDraftStages(visibleStages);
            setOpened(true);
          }}
        >
          <Settings2 size={15} aria-hidden="true" />
          Views & columns
        </button>
      </div>

      <Modal opened={opened} onClose={() => setOpened(false)} title="Opportunity views and columns" centered size="lg">
        <div className="pd-board-config">
          <section>
            <span className="pd-eyebrow">Visible stages</span>
            <h3>Keep the board focused without losing records.</h3>
            <p>Hidden stages remain searchable and can be restored at any time. Every column maps to a stable stage.</p>
            <div className="pd-stage-checkboxes">
              {ALL_OPPORTUNITY_STAGES.map((stage) => (
                <label key={stage}>
                  <input
                    type="checkbox"
                    checked={draftStages.includes(stage)}
                    onChange={(event) => {
                      setDraftStages((stages) =>
                        event.currentTarget.checked
                          ? [...stages, stage]
                          : stages.filter((candidate) => candidate !== stage),
                      );
                    }}
                  />
                  <span>{humanizeStage(stage)}</span>
                </label>
              ))}
            </div>
            {!draftStages.length ? <p className="pd-form-error" role="alert">Keep at least one stage visible.</p> : null}
            <SecondaryButton disabled={!draftStages.length} loading={persist.isPending} onClick={saveColumns}>
              Save visible columns
            </SecondaryButton>
          </section>

          <section className="pd-save-view">
            <span className="pd-eyebrow">Saved view</span>
            <h3>Save this filter and stage set.</h3>
            <p>The current search text and visible stages travel with your candidate account across devices.</p>
            <TextInput
              label="View name"
              value={viewName}
              onChange={(event) => setViewName(event.currentTarget.value)}
              placeholder="Example: Active interviews"
              maxLength={80}
            />
            <div className="pd-form-actions">
              {current.activeViewId ? (
                <SecondaryButton
                  color="red"
                  leftSection={<Trash2 size={15} aria-hidden="true" />}
                  onClick={removeActive}
                >
                  Delete active view
                </SecondaryButton>
              ) : null}
              <PrimaryButton
                leftSection={<Bookmark size={15} aria-hidden="true" />}
                disabled={!viewName.trim()}
                loading={persist.isPending}
                onClick={saveView}
              >
                Save current view
              </PrimaryButton>
            </div>
          </section>
          {persist.isError ? (
            <p className="pd-form-error" role="alert">
              {persist.error instanceof Error ? persist.error.message : 'The board settings could not be saved.'}
            </p>
          ) : null}
        </div>
      </Modal>
    </>
  );
};
